import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Mail, Phone, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { TID } from "@/constants/testIds/dv";
import { BRAND } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  new: "bg-[#E63946] text-white",
  contacted: "bg-amber-500 text-white",
  closed: "bg-neutral-700 text-white",
};

const Admin = () => {
  const { t, lang, setLang } = useLanguage();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/quotes`);
      setQuotes(r.data || []);
    } catch (_) {
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status });
      setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (_) {
      // ignore
    }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/quotes/${id}`);
      setQuotes((q) => q.filter((x) => x.id !== id));
    } catch (_) {
      // ignore
    }
  };

  const fmt = (s) => {
    try {
      return new Date(s).toLocaleString();
    } catch {
      return s;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-black text-white border-b border-white/10">
        <div className="container-x flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="DV" className="h-10 w-auto" />
            <div>
              <div className="font-display uppercase text-xl tracking-tight">Admin</div>
              <div className="text-[10px] tracking-[0.3em] text-[#E63946] font-semibold">
                DV Solutions
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center rounded-sm border border-white/20 overflow-hidden text-[11px] font-semibold">
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 uppercase tracking-widest ${
                  lang === "en" ? "bg-[#E63946] text-white" : "text-white/70"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("es")}
                className={`px-3 py-1.5 uppercase tracking-widest ${
                  lang === "es" ? "bg-[#E63946] text-white" : "text-white/70"
                }`}
              >
                ES
              </button>
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm uppercase tracking-widest font-semibold"
            >
              <ArrowLeft className="h-4 w-4" /> {t("admin.back")}
            </Link>
          </div>
        </div>
      </header>

      <main className="container-x py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="h-px w-10 bg-[#E63946]" />
            <span className="label-eyebrow">DV Solutions</span>
          </div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">
            {t("admin.title")}
          </h1>
          <p className="text-muted-foreground mt-2">{t("admin.subtitle")}</p>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : quotes.length === 0 ? (
          <div
            data-testid={TID.adminEmpty}
            className="border border-dashed border-border p-10 text-center text-muted-foreground rounded-sm"
          >
            {t("admin.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto border border-border rounded-sm">
            <table data-testid={TID.adminTable} className="min-w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  <th className="text-left p-3 uppercase tracking-widest text-[11px] font-semibold">
                    {t("admin.headers.date")}
                  </th>
                  <th className="text-left p-3 uppercase tracking-widest text-[11px] font-semibold">
                    {t("admin.headers.name")}
                  </th>
                  <th className="text-left p-3 uppercase tracking-widest text-[11px] font-semibold">
                    {t("admin.headers.contact")}
                  </th>
                  <th className="text-left p-3 uppercase tracking-widest text-[11px] font-semibold">
                    {t("admin.headers.service")}
                  </th>
                  <th className="text-left p-3 uppercase tracking-widest text-[11px] font-semibold">
                    {t("admin.headers.message")}
                  </th>
                  <th className="text-left p-3 uppercase tracking-widest text-[11px] font-semibold">
                    {t("admin.headers.status")}
                  </th>
                  <th className="text-left p-3 uppercase tracking-widest text-[11px] font-semibold">
                    {t("admin.headers.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr
                    key={q.id}
                    data-testid={TID.adminRow(q.id)}
                    className="border-t border-border align-top hover:bg-muted/40"
                  >
                    <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">
                      {fmt(q.created_at)}
                    </td>
                    <td className="p-3 font-semibold">{q.name}</td>
                    <td className="p-3 text-xs">
                      <a
                        href={`mailto:${q.email}`}
                        className="flex items-center gap-1 hover:text-[#E63946]"
                      >
                        <Mail className="h-3 w-3" /> {q.email}
                      </a>
                      <a
                        href={`tel:${q.phone}`}
                        className="flex items-center gap-1 hover:text-[#E63946] mt-1"
                      >
                        <Phone className="h-3 w-3" /> {q.phone}
                      </a>
                    </td>
                    <td className="p-3 capitalize">{q.service?.replace(/_/g, " ")}</td>
                    <td className="p-3 max-w-md text-xs text-muted-foreground">
                      {q.message || "—"}
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest rounded-sm font-semibold ${
                          STATUS_BADGE[q.status] || STATUS_BADGE.new
                        }`}
                      >
                        {q.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1.5">
                        <button
                          data-testid={TID.adminMarkContacted(q.id)}
                          onClick={() => updateStatus(q.id, "contacted")}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest border border-border px-2 py-1 hover:border-amber-500 hover:text-amber-600 rounded-sm"
                        >
                          <CheckCircle2 className="h-3 w-3" /> {t("admin.mark_contacted")}
                        </button>
                        <button
                          data-testid={TID.adminMarkClosed(q.id)}
                          onClick={() => updateStatus(q.id, "closed")}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest border border-border px-2 py-1 hover:border-neutral-700 hover:text-neutral-800 rounded-sm"
                        >
                          <XCircle className="h-3 w-3" /> {t("admin.mark_closed")}
                        </button>
                        <button
                          data-testid={TID.adminDelete(q.id)}
                          onClick={() => del(q.id)}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest border border-border px-2 py-1 hover:border-[#E63946] hover:text-[#E63946] rounded-sm"
                        >
                          <Trash2 className="h-3 w-3" /> {t("admin.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
